import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Info, ArrowUpDown } from "lucide-react";
import api from "../api";
import VehicleCard from "./VehicleCard";
import { SectionHead, Reveal } from "./Reveal";
import { CATEGORIES } from "../constants";
import { useLanguage } from "../i18n/LanguageContext";

const Fleet = ({ onOpenVehicle, onEnquire }) => {
  const { t } = useLanguage();
  const [vehicles, setVehicles] = useState([]);
  const [status, setStatus] = useState("loading");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("featured");
  const [noDepositOnly, setNoDepositOnly] = useState(false);

  const load = () => {
    setStatus("loading");
    api
      .get("/vehicles")
      .then((res) => {
        setVehicles(res.data);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  };

  useEffect(load, []);

  useEffect(() => {
    const handler = () => {
      setNoDepositOnly(true);
      setCategory("all");
    };
    window.addEventListener("aa:no-deposit-filter", handler);
    return () => window.removeEventListener("aa:no-deposit-filter", handler);
  }, []);

  const availableCategories = useMemo(
    () => ["all", ...CATEGORIES.filter((c) => vehicles.some((v) => v.category === c))],
    [vehicles]
  );

  const filtered = useMemo(() => {
    let list = vehicles;
    if (category !== "all") list = list.filter((v) => v.category === category);
    if (noDepositOnly) list = list.filter((v) => v.no_deposit_eligible);
    const sorted = [...list];
    if (sort === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "priceAsc") sorted.sort((a, b) => (a.price_per_day ?? Infinity) - (b.price_per_day ?? Infinity));
    if (sort === "priceDesc")
      sorted.sort((a, b) => (b.price_per_day ?? -Infinity) - (a.price_per_day ?? -Infinity));
    if (sort === "featured")
      sorted.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || Number(b.featured) - Number(a.featured));
    return sorted;
  }, [vehicles, category, sort, noDepositOnly]);

  return (
    <section id="fleet" data-testid="fleet-section" className="relative mx-auto max-w-7xl scroll-mt-24 px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHead eyebrow={t("fleet.eyebrow")} title={t("fleet.title")} sub={t("fleet.sub")} />
      </div>

      <Reveal delay={0.1} className="mt-10">
        <div className="flex flex-wrap items-center gap-2.5" role="tablist" aria-label={t("fleet.title")}>
          {availableCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={category === cat && !noDepositOnly}
              data-testid={`filter-${cat}`}
              onClick={() => {
                setCategory(cat);
                setNoDepositOnly(false);
              }}
              className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${
                category === cat && !noDepositOnly
                  ? "border-gold bg-gold text-[#08080A] shadow-[0_0_24px_rgba(212,175,55,0.3)]"
                  : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-gold/40 hover:text-gold"
              }`}
            >
              {t(`categories.${cat}`)}
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={noDepositOnly}
            data-testid="filter-no-deposit"
            onClick={() => {
              setNoDepositOnly((v) => !v);
              setCategory("all");
            }}
            className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${
              noDepositOnly
                ? "border-gold bg-gold/15 text-gold shadow-[0_0_24px_rgba(212,175,55,0.2)]"
                : "border-gold/30 bg-transparent text-gold/80 hover:bg-gold/10"
            }`}
          >
            {t("fleet.noDepositOnly")}
          </button>

          <label className="ms-auto flex items-center gap-2 text-xs text-slate-400">
            <ArrowUpDown className="h-3.5 w-3.5 text-gold/70" aria-hidden="true" />
            <span className="sr-only">{t("fleet.sort")}</span>
            <select
              data-testid="fleet-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-white/10 bg-[#0c0c10] px-4 py-2 text-xs font-semibold text-slate-200 outline-none transition-colors focus:border-gold/50"
            >
              <option value="featured">{t("fleet.sortFeatured")}</option>
              <option value="name">{t("fleet.sortName")}</option>
              <option value="priceAsc">{t("fleet.sortPriceAsc")}</option>
              <option value="priceDesc">{t("fleet.sortPriceDesc")}</option>
            </select>
          </label>
        </div>
      </Reveal>

      {status === "loading" && (
        <div data-testid="fleet-loading" className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse overflow-hidden rounded-2xl border border-white/[0.05] bg-panel">
              <div className="aspect-[16/10] bg-white/[0.04]" />
              <div className="space-y-3 p-5">
                <div className="h-3 w-20 rounded bg-white/[0.06]" />
                <div className="h-6 w-40 rounded bg-white/[0.08]" />
                <div className="h-9 w-full rounded-full bg-white/[0.05]" />
              </div>
            </div>
          ))}
          <span className="sr-only">{t("fleet.loading")}</span>
        </div>
      )}

      {status === "error" && (
        <div data-testid="fleet-error" className="mt-12 rounded-2xl border border-red-500/20 bg-red-500/5 p-10 text-center">
          <p className="text-slate-300">{t("fleet.loadError")}</p>
          <button
            type="button"
            data-testid="fleet-retry-button"
            onClick={load}
            className="mt-4 rounded-full border border-gold/40 px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-gold hover:bg-gold hover:text-[#08080A]"
          >
            {t("fleet.retry")}
          </button>
        </div>
      )}

      {status === "ok" && (
        <>
          <motion.div layout data-testid="fleet-grid" className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filtered.map((vehicle, i) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} index={i} onOpen={onOpenVehicle} onEnquire={onEnquire} />
              ))}
            </AnimatePresence>
          </motion.div>
          {filtered.length === 0 && (
            <p data-testid="fleet-no-results" className="mt-12 text-center text-slate-400">
              {t("fleet.noResults")}
            </p>
          )}
          <Reveal className="mt-12">
            <p data-testid="fleet-sample-note" className="flex items-start gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-xs leading-relaxed text-slate-500">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold/60" aria-hidden="true" />
              {t("fleet.sampleNote")}
            </p>
          </Reveal>
        </>
      )}
    </section>
  );
};

export default Fleet;
